import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import UploadCoverPhotoPage from './UploadCoverPhotoPage';
import { useDispatch } from 'react-redux';
import { clearSketch } from '../../../redux/slice/sketchSlice';
import { useSelector } from 'react-redux';
import { resetStep } from '../../../redux/slice/stepSlice';
import {
  addToShopifyCart,
  getCartLineMongoIdFromAddResponse,
  recordCartLineId,
  cartLineShouldRemainForBook,
} from '../../../services/shopifyCartService';
import { toast } from 'react-toastify';
import { setTitle } from '../../../helpers/MetaTag';
import { getMyGifts, giftIdOfBook, giftPagePath } from '../../../services/giftService';
import { useConsumerPdfDownload } from '../../../hooks/useConsumerPdfDownload';
import { BookOpen, Download, FileText, Gift, Lock, Plus, Printer, ShoppingBag, Trash2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Popup from '../../../components/ui/Popup';
import Sheet from '../../../components/ui/Sheet';
import EmptyState from '../../../components/ui/EmptyState';
import ErrorState from '../../../components/ui/ErrorState';
import Skeleton, { SkeletonGroup } from '../../../components/ui/Skeleton';
import WizardShell from '../../../components/ui/WizardShell';
import BookCard from '../../../components/books/BookCard';
import { describeBook, FILTERS, matchesFilter, routeForBook, stepNumberOf } from '../../../components/books/bookStatus';
import { BUILDER_STEPS } from '../../../components/builder/builderSteps';
import { showMessageBar } from '../../../util/messageBar';
import { readLocalCartBookIds } from '../../../services/shopifyCartService';
import { bookDelete } from '../../../services/bookService';
import { removeBook, markStepAsVisited, selectBooksLoadedAt } from '../../../redux/slice/profileSlice';
import { setActiveBookId, setFetchingImages } from '../../../redux/slice/stepSlice';
import { setCoverImages, setImages } from '../../../redux/slice/sketchSlice';
import { fetchBatchImagesByBookId } from '../../../services/mediaService';
import { saveStep } from '../../../services/stepService';
import { loadUserData } from '../../../util/navigationUtils';
import { BASE_BOOK_PRICE, PDF_DOWNLOAD_PRICE } from '../../../config/pricing';
import { withBookTag, currentUrlBookTag, sanitizeBookTag } from '../../../util/bookTag';
import { canSwitchFormat, isPdfBook, switchBookFormat } from '../../../util/bookFormat';
import { isEtsyUser } from '../../../services/etsyService';
import {
  fetchAllOrderPdfsForUser,
  buildBookIdToConsumerOrderMap,
} from '../../../services/orderPdfsService';

const UserLandingPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { activeBookId } = useSelector((state) => state.stepFlow);

  // The ordered book whose "This book has been ordered" pop-up is open.
  const [lockedBook, setLockedBook] = useState(null);
  const [filter, setFilter] = useState('all');
  // The book whose ⋯ sheet is open, and the one waiting for delete confirmation.
  const [menuBook, setMenuBook] = useState(null);
  // The ⋯ menu is a sheet on phones and a dropdown under the ⋯ button from tablet up.
  const phoneQuery = '(max-width: 767.98px)';
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(phoneQuery).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(phoneQuery);
    if (!mq) return undefined;
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  const askSwitch = (book) => {
    setSwitchBook(book);
    setMenuBook(null);
  };
  const askDelete = (book) => {
    setConfirmBook(book);
    setMenuBook(null);
  };
  /** The dropdown's items (TabBooksFormat, DeskBooksFormat): the format switch, then Delete in red. */
  const dropdownItems = (book) => (
    <>
      {canSwitchFormat(book) ? (
        <button
          type='button'
          role='menuitem'
          onClick={() => askSwitch(book)}
          className='flex min-h-[52px] w-full items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-left text-md-dark hover:bg-[#FBF8F3] focus:outline-none focus-visible:bg-[#FBF8F3]'
        >
          {isPdfBook(book) ? (
            <Printer className='h-[18px] w-[18px] flex-shrink-0 text-md-pink-dark' aria-hidden='true' />
          ) : (
            <FileText className='h-[18px] w-[18px] flex-shrink-0 text-md-pink-dark' aria-hidden='true' />
          )}
          <span className='flex flex-col gap-px'>
            <span className='text-[15px] font-bold'>{isPdfBook(book) ? 'Switch to a printed book' : 'Switch to PDF only'}</span>
            <span className='text-[13px] font-medium text-md-muted'>{isPdfBook(book) ? 'From $24.95 plus shipping' : '$9.95, nothing printed or shipped'}</span>
          </span>
        </button>
      ) : null}
      <button
        type='button'
        role='menuitem'
        onClick={() => askDelete(book)}
        className='flex min-h-11 w-full items-center gap-2.5 rounded-[10px] px-3 text-[15px] font-bold text-red-700 hover:bg-red-50 focus:outline-none focus-visible:bg-red-50'
      >
        <Trash2 className='h-[18px] w-[18px] flex-shrink-0' aria-hidden='true' />
        Delete book
      </button>
    </>
  );
  // My Books ⋯ menu: switch an unordered book between printed and PDF only (designs BooksFormat, BooksToPrint).
  const [switchBook, setSwitchBook] = useState(null);
  const [switching, setSwitching] = useState(false);
  const switchToPdf = !!switchBook && !isPdfBook(switchBook);
  const confirmSwitch = async () => {
    if (!switchBook || switching) return;
    setSwitching(true);
    const res = await switchBookFormat(dispatch, switchBook._id, switchToPdf ? 'pdf' : 'print');
    setSwitching(false);
    if (!res?.success) {
      toast.error(res?.message || "We couldn't change that. Please try again.");
      return;
    }
    setSwitchBook(null);
    showMessageBar({ text: switchToPdf ? 'Switched to PDF only' : 'Switched to a printed book' });
  };
  const [confirmBook, setConfirmBook] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloaded, setDownloaded] = useState(() => new Set());
  const [retrying, setRetrying] = useState(false);

  const profileData = useSelector((state) => state.profile.data);
  // An ordered book can get the format it lacks (designs BooksOrdered): never gift books or Etsy
  // buyers, who paid in their own format (the API refuses those switches).
  const canBuyOtherFormat = (book) => canSwitchFormat(book) && !isEtsyUser(profileData);
  const profileUserId = profileData?._id;

  const searchParams = new URLSearchParams(window.location.search);
  const stepCode = searchParams.get('stepCode');

  useEffect(() => {
    if (location.pathname === '/dashboard' && !(!!activeBookId && !!stepCode)) {
      setTitle('Memory Doodle | My Books');
    }
  }, [location.pathname, activeBookId, stepCode]);

  const onNavigateCreateBook = () => {
    dispatch(resetStep());
    dispatch(clearSketch());
    // A customer who arrived on a PDF link and was routed here still has PDF
    // intent; the book they start next is the one they came to buy.
    navigate(withBookTag('/create-book?stepCode=Create_New_Book', currentUrlBookTag()));
  };

  // An unfinished Shopify gift, if any: a strip at the top so a recipient can always get back to it, also
  // before any gift book exists or after signing in another way (put back 2026-10-23).
  const [openGifts, setOpenGifts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getMyGifts().then((res) => {
      if (!cancelled && res?.success) setOpenGifts((res.data || []).filter((g) => g.open));
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const { myBooks } = useSelector((state) => state.profile);

  // Prefer book.consumerPdfOrderId from API (fast). Only load all orders when any book
  // still needs legacy resolution (e.g. before backfill or edge cases).
  const needsLegacyOrderPdfLookup = useMemo(
    () => (myBooks ?? []).some((b) => !b.consumerPdfOrderId),
    [myBooks],
  );

  const { data: ordersWithPdfMeta } = useFetch({
    dependencies: ['orderPdfsLegacyLookup', profileUserId],
    enabled: !!profileUserId && needsLegacyOrderPdfLookup,
    fetchFunction: fetchAllOrderPdfsForUser,
  });

  const bookIdToConsumerOrder = useMemo(
    () => buildBookIdToConsumerOrderMap(ordersWithPdfMeta || []),
    [ordersWithPdfMeta],
  );

  const { downloadConsumerPdf, downloadingOrderId } = useConsumerPdfDownload();


  // Helper to get variant ID from a cart item
  const getVariantId = (item) => {
    if (!item || typeof item !== 'object') return null;
    return item.shopifyVariantId || item.variantId || item.id || null;
  };

  //  NEW: Add to cart functionality (cloned from ReviewBookContent)
  const handleAddToCart = (bookId, navigateToCart = true) => {
    try {
      const bookData = myBooks.find(({ _id }) => bookId === _id);

      if (!bookData) {
        toast.error('Book data not found. Please try again.');
        return;
      }

      // Check if book has cover image
      if (!bookData.coverImage) {
        toast.error(
          "This book doesn't have a cover image yet. Please complete your book first.",
        );
        return;
      }

      // A PDF-only book goes into the order like any book (decided 2026-10-26); the cart
      // prices it as the $9.95 PDF and checks it out as the PDF product.

      // STEP 1: Get existing cart
      let existingCart = [];
      try {
        const raw = localStorage.getItem('selectedPlan');
        if (raw) {
          const parsed = JSON.parse(raw);
          existingCart = Array.isArray(parsed) ? parsed : [parsed];
        }
      } catch (e) {
        console.error('Error reading cart:', e);
        existingCart = [];
      }

      const staleIdx = existingCart.findIndex((item) => item.bookId === bookId);
      if (
        staleIdx >= 0 &&
        !cartLineShouldRemainForBook(existingCart[staleIdx], bookData)
      ) {
        existingCart.splice(staleIdx, 1);
        if (!existingCart.length) {
          localStorage.removeItem('selectedPlan');
        } else {
          localStorage.setItem('selectedPlan', JSON.stringify(existingCart));
        }
        window.dispatchEvent(new Event('cartUpdated'));
      }

      // Helper to update local storage and trigger async API sync
      const updateLocalAndSync = (cartArray, itemToSync, isIncrement = false) => {
        localStorage.setItem('selectedPlan', JSON.stringify(cartArray));
        window.dispatchEvent(new Event('cartUpdated'));

        // Fire & forget async API call
        if (itemToSync) {
          const payload = {
            bookId: itemToSync.bookId,
            shopifyVariantId: itemToSync.shopifyVariantId || itemToSync.variantId || itemToSync.id,
            shopifyProductId: itemToSync.shopifyProductId || itemToSync.productId || "",
            quantity: isIncrement ? 1 : (itemToSync.quantity || 1), // If incrementing, API just needs +1 to its existing logic
            title: itemToSync.title,
            name: itemToSync.name,
            description: itemToSync.description,
            image: itemToSync.image,
            previewUrl: itemToSync.previewUrl,
            coverImage: itemToSync.coverImage,
            planPricingData: itemToSync.planPricingData,
            recipient: itemToSync.recipient,
            version: itemToSync.version,
            features: itemToSync.features,
            offerType: itemToSync.offerType,
            highLightTag: itemToSync.highLightTag,
            highLightText: itemToSync.highLightText
          };

          addToShopifyCart(payload)
            .then(res => recordCartLineId(itemToSync, getCartLineMongoIdFromAddResponse(res)))
            .catch(err => console.error("Async cart add failed:", err));
        }
      };

      // STEP 2: Check if book already exists in cart and increment quantity
      const existingBookIndex = existingCart.findIndex(
        (item) => item.bookId === bookId,
      );

      if (existingBookIndex >= 0) {
        // Book found - increment quantity
        existingCart[existingBookIndex].quantity += 1;
        existingCart[existingBookIndex].planPricingData.quantity += 1;
        if (!existingCart[existingBookIndex].createdAt) {
          existingCart[existingBookIndex].createdAt = new Date().toISOString();
        }

        updateLocalAndSync(existingCart, existingCart[existingBookIndex], true);

        if (navigateToCart) {
          navigate('/finalize-order');
        }
        return;
      }

      // STEP 3: Check for unassigned plans first (original flow)
      let unassignedPlans = [];
      try {
        const raw = localStorage.getItem('unassignedPlans');
        if (raw) {
          const parsed = JSON.parse(raw);
          unassignedPlans = Array.isArray(parsed) ? parsed : [parsed];
        }
      } catch (e) {
        console.error('Error reading unassigned plans:', e);
      }

      // If unassigned plans exist, use original flow
      if (unassignedPlans.length > 0) {
        console.log('Unassigned plans found, using original flow');

        unassignedPlans.forEach((plan) => {
          const planVariantId = getVariantId(plan);

          if (!planVariantId) {
            console.warn('Skipping plan without variant ID:', plan);
            return;
          }

          const existingIndex = existingCart.findIndex(
            (item) =>
              item.bookId === bookId && getVariantId(item) === planVariantId,
          );

          if (existingIndex >= 0) {
            existingCart[existingIndex].quantity += 1;
            existingCart[existingIndex].planPricingData.quantity += 1;
            console.log(
              `Incremented quantity for existing plan (${planVariantId})`,
            );
          } else {
            const cartItem = {
              id: `book-${bookId}-${planVariantId}`,
              bookId: bookId,
              title:
                plan.title ||
                plan.name ||
                bookData.title ||
                'Custom Coloring Book',
              name: plan.name || plan.title,
              description: plan.description,
              quantity: plan.quantity || 1,
              shopifyVariantId: plan.shopifyVariantId || planVariantId,
              shopifyProductId: plan.shopifyProductId || plan.productId,
              planPricingData: {
                price: plan.planPricingData?.price || BASE_BOOK_PRICE,
                unitPrice: plan.planPricingData?.unitPrice || BASE_BOOK_PRICE,
                quantity: plan.quantity || 1,
                priceDescription:
                  plan.planPricingData?.priceDescription || 'Select Plan',
                currencyCode: plan.planPricingData?.currencyCode || 'USD',
              },
              image:
                plan.image ||
                bookData.coverImageThumbnail ||
                bookData.coverImage ||
                bookData.previewUrl,
              highLightTag: plan.highLightTag || 0,
              highLightText: plan.highLightText || '',
              features: plan.features || [],
              offerType: plan.offerType,
              recipient: bookData.recipientName || '',
              version: bookData.version || 1,
              previewUrl:
                bookData.previewUrl || bookData.coverImageThumbnail || bookData.coverImage,
              coverImage:
                bookData.coverImageThumbnail || bookData.coverImage || bookData.previewUrl,
              createdAt: new Date().toISOString(),
            };

            existingCart.push(cartItem);
            console.log(`Added new plan to cart for book ${bookId}`);
            // Fire async call for this specific new item
            updateLocalAndSync(existingCart, cartItem);
          }
        });

        localStorage.removeItem('unassignedPlans');

        console.log('Cart updated with unassigned plans:', existingCart);

        if (navigateToCart) {
          navigate('/finalize-order');
        }
        return;
      }

      // NEW LOGIC: No unassigned plans - Add book with base plan
      if (existingCart.length === 0) {
        console.log('Empty cart detected, adding book with base plan');
      } else {
        // Check if all variants are the same
        const allVariantIds = existingCart
          .map(getVariantId)
          .filter((id) => id !== null);

        if (allVariantIds.length === 0) {
          console.log('No valid variants in cart, adding book with base plan');
        } else {
          const firstVariantId = allVariantIds[0];
          const allSameVariant = allVariantIds.every(
            (id) => id === firstVariantId,
          );

          if (allSameVariant) {
            // All same variant - Clone plan for new book
            console.log('All same variant, cloning plan for new book');

            const templatePlan = existingCart[0];
            const variantId = getVariantId(templatePlan);

            const cartItem = {
              id: `book-${bookId}-${variantId}`,
              bookId: bookId,
              title:
                templatePlan.title ||
                templatePlan.name ||
                bookData.title ||
                'Custom Coloring Book',
              name: templatePlan.name || templatePlan.title,
              description: templatePlan.description,
              quantity: 1,
              shopifyVariantId: variantId,
              shopifyProductId:
                templatePlan.shopifyProductId || templatePlan.productId,
              planPricingData: {
                price: templatePlan.planPricingData?.price || BASE_BOOK_PRICE,
                unitPrice: templatePlan.planPricingData?.unitPrice || BASE_BOOK_PRICE,
                quantity: 1,
                priceDescription:
                  templatePlan.planPricingData?.priceDescription ||
                  'Select Plan',
                currencyCode:
                  templatePlan.planPricingData?.currencyCode || 'USD',
              },
              image:
                templatePlan.image ||
                bookData.coverImageThumbnail ||
                bookData.coverImage ||
                bookData.previewUrl,
              highLightTag: templatePlan.highLightTag || 0,
              highLightText: templatePlan.highLightText || '',
              features: templatePlan.features || [],
              offerType: templatePlan.offerType,
              recipient: bookData.recipientName || '',
              version: bookData.version || 1,
              previewUrl:
                bookData.previewUrl || bookData.coverImageThumbnail || bookData.coverImage,
              coverImage:
                bookData.coverImageThumbnail || bookData.coverImage || bookData.previewUrl,
              createdAt: new Date().toISOString(),
            };

            existingCart.push(cartItem);
            updateLocalAndSync(existingCart, cartItem);

            console.log('Cart updated with cloned plan:', existingCart);

            if (navigateToCart) {
              navigate('/finalize-order');
            }
            return;
          } else {
            console.log('Mixed variants detected, adding book with base plan');
          }
        }
      }

      // ADD BOOK WITH BASE PLAN
      const basePlan = {
        id: 'gid://shopify/ProductVariant/47524057547000',
        productId: 'gid://shopify/Product/9314844639480',
        variantId: 'gid://shopify/ProductVariant/47524057547000',
        name: 'Personalized Coloring Book',
        title: 'Personalized Coloring Book',
        description:
          "Turn your treasured family photos into a beautiful, 20-page custom coloring book. A one-of-a-kind gift that transforms your favorite memories into a shared activity, perfect for connecting with grandparents, parents, and children. Each book is a tangible piece of your family's story that you can create and color together. How It Works Choose Your Package: Select the perfect option for you from the choices below. Upload Your Photos: After ordering, you'll receive a link to easily upload your 20 favorite photos. We Create & Ship: Our artists design your custom book, and we ship the heirloom-quality final product directly to your door. What's Included in Every Book Heirloom-Quality 20-Page Custom Coloring Book Personalized Foreword Page with Your Message Custom Coloring-Style Cover Page from Your Photo",
        quantity: 1,
        planPricingData: {
          price: BASE_BOOK_PRICE,
          unitPrice: BASE_BOOK_PRICE,
          quantity: 1,
          priceDescription: 'Select Plan',
          currencyCode: 'USD',
        },
        image:
          'https://cdn.shopify.com/s/files/1/0780/2827/5960/files/Product_Image_1.jpg?v=1758836019',
        highLightTag: 0,
        highLightText: 'Most Popular',
        features: [],
      };

      const cartItem = {
        ...basePlan,
        id: `book-${bookId}-${basePlan.variantId}`,
        bookId: bookId,
        shopifyVariantId: basePlan.variantId,
        shopifyProductId: basePlan.productId,
        recipient: bookData.recipientName || '',
        version: bookData.version || 1,
        previewUrl:
          bookData.previewUrl || bookData.coverImageThumbnail || bookData.coverImage,
        coverImage:
          bookData.coverImageThumbnail || bookData.coverImage || bookData.previewUrl,
        createdAt: new Date().toISOString(),
      };

      existingCart.push(cartItem);
      updateLocalAndSync(existingCart, cartItem);

      console.log('Cart updated with base plan:', existingCart);

      if (navigateToCart) {
        navigate('/finalize-order');
      }
    } catch (error) {
      console.error('Error adding book to cart:', error);
      toast.error('Failed to add book to order. Please try again.');
    }
  };

  // ---- My Books on the redesign (docs/design/HANDOFF.md: Books, TabBooks, DeskBooks)

  const { booksLoading } = useSelector((state) => state.profile);
  const booksLoadedAt = useSelector(selectBooksLoadedAt);
  const { session } = useSelector((state) => state.authProvider);
  const sessionUserId = session?.user?._id;
  const loadFailed = !booksLoading && !booksLoadedAt && session?.status === 'authorized';
  const cartBookIds = readLocalCartBookIds();
  // Re-read the order when another part of the page (or another tab) changes it.
  const [, setCartVersion] = useState(0);
  useEffect(() => {
    const bump = () => setCartVersion((v) => v + 1);
    window.addEventListener('cartUpdated', bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener('cartUpdated', bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  // The step a book is on lives on the profile's book summary, not on myBooks.
  const stepOf = new Map((profileData?.bookData || []).map((b) => [String(b._id), b.latestStepCode]));
  const books = (myBooks || []).map((raw) => {
    const book = raw.latestStepCode ? raw : { ...raw, latestStepCode: stepOf.get(String(raw._id)) };
    return { book, info: describeBook(book, { cartBookIds }) };
  });
  const counts = {
    all: books.length,
    progress: books.filter(({ info }) => info.status !== 'ordered').length,
    ordered: books.filter(({ info }) => info.status === 'ordered').length,
  };
  const shown = books.filter(({ info }) => matchesFilter(info.status, filter));
  // Books in the order (any of the customer's books in the local cart, reorders included) for the order card.
  const orderCount = booksLoading ? 0 : books.filter(({ book }) => cartBookIds.has(String(book._id))).length;
  const orderText = `${orderCount} ${orderCount === 1 ? 'book' : 'books'} in your order`;

  /** Open a book on the step it is on (what tapping a card does). */
  const openBookAtStep = (book) => {
    window.stop();
    dispatch(setFetchingImages(true));
    fetchBatchImagesByBookId(book._id)
      .then((res) => {
        const rows = res?.docs?.data || [];
        if (rows.length) {
          dispatch(setCoverImages(rows.filter((image) => !!image.isCover)));
          dispatch(setImages(rows.filter((image) => !image.isCover)));
        }
      })
      .finally(() => dispatch(setFetchingImages(false)));
    const step = BUILDER_STEPS[stepNumberOf(book) - 1] || BUILDER_STEPS[0];
    saveStep({ userId: sessionUserId, bookId: book._id, isCompleted: 'yes', stepCode: step.stepCode });
    dispatch(markStepAsVisited({ id: book._id, currentStepCode: step.stepCode }));
    dispatch(setActiveBookId({ bookId: book._id }));
    navigate(withBookTag(routeForBook(book), sanitizeBookTag(book.tag)));
  };

  const openBook = (book, info) => {
    if (info.status === 'ordered') return setLockedBook(book);
    if (info.status === 'gift') return navigate(giftPagePath(giftIdOfBook(book)));
    return openBookAtStep(book);
  };

  // Add an ordered book to the order in `format` ("print" | "pdf"). The format lives on the book,
  // so switch it first when it differs; the past order is never changed. Already in the order in
  // the other format: open Review order instead, which offers that book's combinations ("Add the
  // printed book" on a PDF line, the $5 PDF on a printed one) rather than converting its line.
  const [buyingId, setBuyingId] = useState(null);
  const buyFormat = async (book, format) => {
    if (!book?._id || buyingId) return;
    if (isPdfBook(book) !== (format === 'pdf')) {
      if (readLocalCartBookIds().has(String(book._id))) {
        navigate('/finalize-order');
        return;
      }
      setBuyingId(book._id);
      const res = await switchBookFormat(dispatch, book._id, format);
      setBuyingId(null);
      if (!res?.success) {
        toast.error(res?.message || "We couldn't add that. Please try again.");
        return;
      }
    }
    handleAddToCart(book._id, true);
  };

  const runAction = (book, info) => {
    switch (info.status) {
      case 'draft':
        return openBookAtStep(book);
      case 'gift':
        return navigate(giftPagePath(giftIdOfBook(book)));
      case 'cart':
        return navigate('/finalize-order');
      case 'ready':
        return handleAddToCart(book._id, true);
      case 'ordered':
        // Order again buys the printed book; "Get the PDF" may have left the book on PDF.
        return buyFormat(book, 'print');
      default:
        return undefined;
    }
  };

  // "This book has been ordered" pop-up: its buttons follow what was bought (designs BooksOrdered).
  const lockedInfo = lockedBook ? describeBook(lockedBook) : null;
  const lockedOther = lockedInfo?.otherFormat && canBuyOtherFormat(lockedBook) ? lockedInfo.otherFormat : null;
  const lockedBuy = (format) => {
    const book = lockedBook;
    setLockedBook(null);
    buyFormat(book, format);
  };

  const consumerOrderOf = (book) =>
    book.consumerPdfOrderId ? { _id: book.consumerPdfOrderId, bookTitle: book.title } : bookIdToConsumerOrder.get(String(book._id)) || null;

  const downloadFor = (book) => {
    const order = consumerOrderOf(book);
    if (!order) return null;
    return {
      state: downloadingOrderId === order._id ? 'busy' : downloaded.has(order._id) ? 'done' : 'idle',
      onClick: async () => {
        const ok = await downloadConsumerPdf(order);
        if (ok) setDownloaded((prev) => new Set(prev).add(order._id));
      },
    };
  };

  const deleteBook = async () => {
    const book = confirmBook;
    if (!book || deleting) return;
    setDeleting(true);
    const res = await bookDelete(book._id);
    setDeleting(false);
    if (!res?.success && res?.status !== 200) {
      toast.error(res?.message || "We couldn't delete this book. Please try again.");
      return;
    }
    setConfirmBook(null);
    dispatch(removeBook(book._id));
    showMessageBar({ text: 'Book deleted' });
  };

  const retryLoad = async () => {
    if (!sessionUserId) return;
    setRetrying(true);
    try {
      await loadUserData(sessionUserId, dispatch, false);
    } catch (err) {
      console.error('Retrying the book list failed:', err);
    } finally {
      setRetrying(false);
    }
  };

  if (!!activeBookId && !!stepCode) {
    return <UploadCoverPhotoPage />;
  }

  const emptyTab = { all: 'No books here', progress: 'Nothing in progress', ordered: 'No ordered books yet' }[filter];
  const emptyTabHint = {
    all: '',
    progress: 'Books you start show up here until you order them.',
    ordered: 'Once you order a book, it shows up here so you can order more copies or download the PDF.',
  }[filter];

  return (
    <WizardShell plain className='!pb-10 lg:!pb-16'>
      <div className='flex flex-col gap-3.5'>
        <div className='flex items-center justify-between gap-3'>
          <h1 className='m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]'>My Books</h1>
          <Button size='sm' className='!rounded-xl !px-4 !text-[15px]' onClick={onNavigateCreateBook} icon={<Plus className='h-5 w-5' aria-hidden='true' />}>
            New<span className='hidden md:inline'> coloring</span> book
          </Button>
        </div>

        {/* The order card (Books, TabBooks, DeskBooks; back 2026-10-26): only while the order has a book. */}
        {orderCount ? (
          <button
            type='button'
            onClick={() => navigate('/finalize-order')}
            aria-label={`Review order, ${orderCount} ${orderCount === 1 ? 'book' : 'books'} in your order`}
            className='flex items-center gap-3 rounded-2xl border-2 border-[#E5C5CB] bg-white py-2.5 pl-3 pr-2.5 text-left text-md-dark md:gap-3.5 md:px-5 md:py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink'
          >
            <span aria-hidden='true' className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-md-pink/10 text-md-pink-dark md:h-11 md:w-11'>
              <ShoppingBag className='h-[22px] w-[22px]' />
            </span>
            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <span className='text-base font-bold leading-snug md:text-[17px]'>{orderText}</span>
              <span className='hidden text-sm text-md-muted md:block'>Ready when you are.</span>
            </span>
            <span className='flex h-11 flex-shrink-0 items-center rounded-[10px] bg-md-pink-dark px-4 text-[15px] font-semibold text-white md:px-[18px]'>Review order</span>
          </button>
        ) : null}

        {/* TODO(design): the unfinished-gift strip has no mockup (docs/design/GAPS.md). One strip per open gift
            (decided 2026-10-23); when two come from the same person, the book count tells them apart. */}
        {openGifts.length ? (
          <div className='flex flex-col gap-2'>
            {openGifts.map((gift) => {
              const sameGiver = openGifts.filter((g) => g.giftFrom === gift.giftFrom).length > 1;
              const what = sameGiver ? `Your gift of ${gift.quantity} ${gift.quantity === 1 ? 'book' : 'books'}` : 'Your gift';
              return (
                <button
                  key={gift.giftId}
                  type='button'
                  onClick={() => navigate(giftPagePath(gift.giftId))}
                  className='flex min-h-[56px] items-center gap-3 rounded-[14px] border border-[#E5C5CB] bg-white px-3.5 py-2 text-left text-md-dark'
                >
                  <span aria-hidden='true' className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-md-pink/10 text-md-pink-dark'>
                    <Gift className='h-5 w-5' />
                  </span>
                  <span className='flex-1 text-[15px] font-semibold'>{gift.giftFrom ? `${what} from ${gift.giftFrom} is waiting` : `${what} is waiting for you`}</span>
                  <span className='whitespace-nowrap text-[15px] font-bold text-md-pink-dark'>Go to your gift</span>
                </button>
              );
            })}
          </div>
        ) : null}

        {booksLoading ? (
          <SkeletonGroup label='Loading your books' className='flex flex-col gap-3'>
            <Skeleton shape='block' className='h-14' />
            <div className='flex gap-2'>
              <Skeleton shape='pill' className='w-20' />
              <Skeleton shape='pill' className='w-28' />
              <Skeleton shape='pill' className='w-24' />
            </div>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-[repeat(auto-fill,minmax(220px,1fr))] md:gap-4 lg:gap-5'>
              {[...Array(4)].map((_, i) => (
                <div key={i} className='flex gap-3.5 rounded-[14px] border border-md-pink-light-1 bg-white p-3 md:flex-col'>
                  <Skeleton shape='cover' className='h-24 w-[72px] md:h-auto md:w-full' />
                  <div className='flex flex-1 flex-col gap-2.5 pt-1'>
                    <Skeleton shape='line' className='w-2/3' />
                    <Skeleton shape='pill' className='h-[22px] w-[90px]' />
                    <Skeleton shape='line' className='h-3 w-1/2' />
                    <Skeleton shape='pill' className='h-10 w-[110px] rounded-[10px]' />
                  </div>
                </div>
              ))}
            </div>
          </SkeletonGroup>
        ) : loadFailed ? (
          <ErrorState title="We couldn't load your books" onRetry={retryLoad} retrying={retrying}>
            Check your connection and try again. Your books are still saved.
          </ErrorState>
        ) : books.length === 0 ? (
          <EmptyState icon={<BookOpen className='h-8 w-8' aria-hidden='true' />} title='No books yet' action={<Button onClick={onNavigateCreateBook}>Start your first book</Button>}>
            Pick 20 favorite photos and we'll turn them into a coloring book.
          </EmptyState>
        ) : (
          <>
            <div role='group' aria-label='Filter books' className='flex gap-2 overflow-x-auto'>
              {FILTERS.map((f) => {
                const on = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type='button'
                    aria-pressed={on}
                    onClick={() => setFilter(f.key)}
                    className={`min-h-[40px] flex-shrink-0 rounded-full border px-3.5 text-sm font-semibold ${on ? 'border-md-dark bg-md-dark text-white' : 'border-md-control bg-white text-md-dark'}`}
                  >
                    {f.label} ({counts[f.key]})
                  </button>
                );
              })}
            </div>

            {shown.length === 0 ? (
              <div className='flex flex-col items-center gap-2 rounded-2xl border border-dashed border-md-control bg-white px-5 py-7 text-center'>
                <span className='text-[17px] font-bold text-md-dark'>{emptyTab}</span>
                {emptyTabHint ? <span className='text-sm leading-snug text-md-muted'>{emptyTabHint}</span> : null}
                <Button variant='text' onClick={() => setFilter('all')}>
                  Show all books
                </Button>
              </div>
            ) : (
              <div className='grid grid-cols-1 gap-3 md:grid-cols-[repeat(auto-fill,minmax(220px,1fr))] md:gap-4 lg:gap-5'>
                {shown.map(({ book, info }) => (
                  <BookCard
                    key={book._id}
                    book={book}
                    info={info}
                    coverUrl={book.coverImageThumbnail || book.coverImage || null}
                    onOpen={() => openBook(book, info)}
                    onAction={() => runAction(book, info)}
                    onMore={info.status === 'ordered' ? null : () => setMenuBook(menuBook?._id === book._id ? null : book)}
                    menu={!isPhone && menuBook?._id === book._id ? dropdownItems(book) : null}
                    onCloseMenu={() => setMenuBook(null)}
                    download={info.status === 'ordered' ? downloadFor(book) : null}
                    other={
                      info.otherFormat && canBuyOtherFormat(book)
                        ? { format: info.otherFormat, onClick: () => buyFormat(book, info.otherFormat), busy: buyingId === book._id }
                        : null
                    }
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ⋯ menu, only on books not yet ordered: switch format (not on gift books) above Delete book. */}
      <Sheet open={!!menuBook && isPhone} onClose={() => setMenuBook(null)} label='Book options'>
        <div className='w-full text-left'>
          <div className='px-1 pb-1.5 text-[15px] font-bold text-md-muted'>{menuBook?.title || 'Untitled book'}</div>
          {canSwitchFormat(menuBook) ? (
            <button
              type='button'
              onClick={() => {
                setSwitchBook(menuBook);
                setMenuBook(null);
              }}
              className='mb-2 flex min-h-[54px] w-full items-center gap-3 rounded-xl border border-md-pink-light-1 bg-white px-3.5 py-2 text-left text-md-dark'
            >
              {isPdfBook(menuBook) ? (
                <Printer className='h-5 w-5 flex-shrink-0 text-md-pink-dark' aria-hidden='true' />
              ) : (
                <FileText className='h-5 w-5 flex-shrink-0 text-md-pink-dark' aria-hidden='true' />
              )}
              <span className='flex flex-col'>
                <span className='text-base font-bold'>{isPdfBook(menuBook) ? 'Switch to a printed book' : 'Switch to PDF only'}</span>
                <span className='text-[13px] font-medium text-md-muted'>{isPdfBook(menuBook) ? 'From $24.95 plus shipping' : '$9.95, nothing printed or shipped'}</span>
              </span>
            </button>
          ) : null}
          <button
            type='button'
            onClick={() => {
              setConfirmBook(menuBook);
              setMenuBook(null);
            }}
            className='flex min-h-[54px] w-full items-center gap-3 rounded-xl bg-red-50 px-3.5 text-base font-bold text-red-700'
          >
            <Trash2 className='h-5 w-5' aria-hidden='true' />
            Delete book
          </button>
          <Button variant='secondary' fullWidth className='mt-2' onClick={() => setMenuBook(null)}>
            Cancel
          </Button>
        </div>
      </Sheet>

      <Popup
        open={!!switchBook}
        onClose={() => (switching ? null : setSwitchBook(null))}
        icon={switchToPdf ? <FileText className='h-7 w-7' aria-hidden='true' /> : <Printer className='h-7 w-7' aria-hidden='true' />}
        title={switchToPdf ? 'Switch to PDF only?' : 'Switch to a printed book?'}
        actions={[
          <Button key='switch' onClick={confirmSwitch} busy={switching}>
            {switchToPdf ? 'Switch to PDF only' : 'Switch to printed book'}
          </Button>,
          <Button key='keep' variant='secondary' onClick={() => setSwitchBook(null)} disabled={switching}>
            {switchToPdf ? 'Keep the printed book' : 'Keep the PDF'}
          </Button>,
        ]}
      >
        <strong className='text-md-dark'>{switchBook?.title || 'This book'}</strong>{' '}
        {switchToPdf
          ? 'becomes a printable PDF for $9.95, emailed right away. Nothing is printed or shipped. You can switch back anytime before you order.'
          : 'becomes a printed book, from $24.95 plus shipping. You can add the printable PDF for $5 when you review your order.'}
      </Popup>

      <Popup
        open={!!confirmBook}
        onClose={() => (deleting ? null : setConfirmBook(null))}
        alert
        tone='red'
        icon={<Trash2 className='h-7 w-7' aria-hidden='true' />}
        title='Delete this book?'
        actions={[
          <Button key='del' variant='destructive' onClick={deleteBook} busy={deleting}>
            {deleting ? 'Deleting' : 'Delete book'}
          </Button>,
          <Button key='keep' variant='secondary' onClick={() => setConfirmBook(null)} disabled={deleting}>
            Keep it
          </Button>,
        ]}
      >
        <strong className='text-md-dark'>{confirmBook?.title || 'This book'}</strong> will be deleted. This can't be undone.
      </Popup>

      <Popup
        open={!!lockedBook}
        onClose={() => setLockedBook(null)}
        tone='gray'
        icon={<Lock className='h-7 w-7' aria-hidden='true' />}
        title='This book has been ordered'
        actions={[
          ...(lockedInfo?.action
            ? [
                <Button key='again' onClick={() => lockedBuy('print')}>
                  Order again
                </Button>,
              ]
            : []),
          ...(lockedOther === 'print'
            ? [
                <Button key='print' onClick={() => lockedBuy('print')} icon={<Printer className='h-[18px] w-[18px]' aria-hidden='true' />}>
                  Get it printed
                </Button>,
              ]
            : []),
          ...(lockedOther === 'pdf'
            ? [
                <Button
                  key='getpdf'
                  variant='secondary'
                  className='!border-[#E5C5CB] !text-md-pink-dark'
                  onClick={() => lockedBuy('pdf')}
                  icon={<FileText className='h-[18px] w-[18px]' aria-hidden='true' />}
                >
                  Get the PDF · ${PDF_DOWNLOAD_PRICE.toFixed(2)}
                </Button>,
              ]
            : []),
          ...(lockedBook && consumerOrderOf(lockedBook)
            ? [
                <Button
                  key='pdf'
                  variant='secondary'
                  onClick={() => {
                    const d = downloadFor(lockedBook);
                    setLockedBook(null);
                    d?.onClick();
                  }}
                  icon={<Download className='h-[18px] w-[18px]' aria-hidden='true' />}
                >
                  Download PDF
                </Button>,
              ]
            : []),
          // Desktop closes from the corner X (DeskBooksOrdered), leaving the row to the real choices.
          <Button key='close' variant='secondary' className='lg:hidden' onClick={() => setLockedBook(null)}>
            Close
          </Button>,
        ]}
      >
        <strong className='text-md-dark'>{lockedBook?.title || 'This book'}</strong>{' '}
        {lockedInfo?.isPdf
          ? `can't be edited after ordering. You can download it again anytime${lockedOther === 'print' ? ', or get it printed' : ''}.`
          : lockedOther === 'pdf'
            ? "can't be edited after ordering. You can order more copies or get the printable PDF."
            : "can't be edited after ordering. You can still order more copies."}
      </Popup>
    </WizardShell>
  );
};

export default UserLandingPage;